// @figma file=pkCGby54zXzTsccXvHqzO6 node=414:12784 name="Default Input" set="mytaible-shadcn-studio" verified=2026-03-21
import { Loader2, X } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../../lib/utils';

// ─── Size & Variant Maps ──────────────────────────────────────────────────────

const sizeClasses = {
  sm: 'h-7 px-2 py-1 text-base sm:text-xs',
  md: 'h-9 px-3 py-2 text-base sm:text-sm',
  lg: 'h-10 px-4 py-2 text-base',
} as const;

const variantClasses = {
  outline: 'border border-input bg-input-bg shadow-sm',
  filled: 'border border-transparent bg-muted',
  ghost: 'border border-transparent bg-transparent',
} as const;

// Padding adjustments when a slot occupies the left/right edge
const startPad = { sm: 'pl-7', md: 'pl-9', lg: 'pl-10' } as const;
const endPad = { sm: 'pr-7', md: 'pr-9', lg: 'pr-10' } as const;

// Icon/slot element sizing and inset per size
const slotIconSize = { sm: 'size-3.5', md: 'size-4', lg: 'size-5' } as const;
const slotText = { sm: 'text-xs', md: 'text-sm', lg: 'text-base' } as const;
const slotLeft = { sm: 'left-2', md: 'left-3', lg: 'left-3.5' } as const;
const slotRight = { sm: 'right-2', md: 'right-3', lg: 'right-3.5' } as const;

// ─── Props ────────────────────────────────────────────────────────────────────

/**
 * Props for the Input component.
 *
 * Extends all native HTML input attributes except `size` (which is repurposed
 * as a design-token size variant). Pass `type`, `disabled`, `readOnly`,
 * `placeholder`, `value`, `onChange`, `id`, `name`, `required`, `aria-*`, etc.
 * directly.
 */
export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Additional CSS classes merged onto the inner `<input>` element. */
  className?: string;

  /** Field height / text size. Defaults to `'md'`. */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Visual style.
   * - `outline` (default) — border + background
   * - `filled`  — muted background, no visible border
   * - `ghost`   — no border, no background; blends into the surface
   */
  variant?: 'outline' | 'filled' | 'ghost';

  /**
   * Content rendered inside the left edge of the field — typically an icon or
   * a short text symbol such as `<DollarSign className="size-4" />` or
   * `<span>$</span>`. The input receives extra left padding automatically.
   */
  startIcon?: React.ReactNode;

  /**
   * Content rendered inside the right edge of the field — typically an icon,
   * status glyph, or a short suffix such as `<span>.com</span>`.
   * Ignored while `loading` is `true` or when `clearable` shows the × button.
   */
  endIcon?: React.ReactNode;

  /**
   * Short text prefix rendered inside the left edge (e.g. `"$"`, `"https://"`).
   * Takes precedence over `startIcon` when both are supplied.
   */
  startText?: string;

  /**
   * Short text suffix rendered inside the right edge (e.g. `".com"`, `"kg"`).
   * Takes precedence over `endIcon` when both are supplied and no other end
   * slot is active.
   */
  endText?: string;

  /**
   * When `true`, renders a spinner at the right edge and suppresses `endIcon` /
   * `endText` / the clear button. Use while async validation or data-fetch is
   * in flight.
   */
  loading?: boolean;

  /**
   * When `true`, shows an × button at the right edge whenever the field has a
   * non-empty controlled `value`. Clicking it calls `onChange` with
   * `event.target.value = ""`. Requires a controlled `value` prop to activate.
   * Suppressed while `loading` is true, or when the field is `disabled` /
   * `readOnly`.
   */
  clearable?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Single-line text input with AvilaRoffe theme styling.
 *
 * Supports all native HTML input attributes (`type`, `disabled`, `readOnly`,
 * `placeholder`, `value`, `onChange`, `id`, `name`, `required`, `aria-*`).
 * Forwards its ref to the underlying `<input>` element.
 *
 * When `startIcon`, `endIcon`, `startText`, `endText`, `loading`, or
 * `clearable` are provided, the component wraps the `<input>` in a relative
 * container; the ref still points to the inner `<input>`.
 *
 * Error state is driven by `aria-invalid={true}` — set this directly or let
 * `FormControl` / `FieldWrapper` inject it automatically.
 *
 * @example
 * // Basic
 * <Input type="email" placeholder="you@example.com" />
 *
 * @example
 * // With icon and size
 * <Input size="lg" startIcon={<Search className="size-4" />} placeholder="Search…" />
 *
 * @example
 * // Filled variant with error
 * <Input variant="filled" aria-invalid={true} />
 *
 * @example
 * // Loading state
 * <Input loading placeholder="Checking…" />
 *
 * @example
 * // Clearable controlled
 * <Input clearable value={val} onChange={(e) => setVal(e.target.value)} />
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type = 'text',
      size = 'md',
      variant = 'outline',
      startIcon,
      endIcon,
      startText,
      endText,
      loading = false,
      clearable = false,
      onChange,
      value,
      ...props
    },
    ref,
  ) => {
    // End slot priority: loading > showClear > endText > endIcon
    const showClear = clearable && !loading && !props.disabled && !props.readOnly && !!value;

    const handleClear = () => {
      onChange?.({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>);
    };

    const hasStart = !!(startText || startIcon);
    // Use clearable (not showClear) so end padding is always reserved when the
    // clear button can appear — avoids layout shift when the field is empty.
    const hasEnd = !!(loading || clearable || endText || endIcon);

    const inputCls = cn(
      // ── Layout ──────────────────────────────────────────────────────────────
      'flex w-full',
      // ── Size ────────────────────────────────────────────────────────────────
      sizeClasses[size],
      // ── Variant ─────────────────────────────────────────────────────────────
      variantClasses[variant],
      // ── Border radius ───────────────────────────────────────────────────────
      'rounded-[var(--field-radius,var(--radius))]',
      // ── Typography + placeholder ────────────────────────────────────────────
      'text-foreground placeholder:text-muted-foreground',
      // ── Focus ring ──────────────────────────────────────────────────────────
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
      // ── File input reset ────────────────────────────────────────────────────
      'file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground',
      // ── Disabled ────────────────────────────────────────────────────────────
      'disabled:cursor-not-allowed disabled:opacity-50',
      // ── ReadOnly ────────────────────────────────────────────────────────────
      'read-only:bg-muted read-only:cursor-default',
      // ── Error — responds to aria-invalid set by FormControl / FieldWrapper ──
      'aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive',
      // ── Slot padding ────────────────────────────────────────────────────────
      hasStart && startPad[size],
      hasEnd && endPad[size],
      className,
    );

    // ── No slots: render bare <input> (original contract preserved) ────────────
    if (!hasStart && !hasEnd) {
      return (
        <input
          ref={ref}
          type={type}
          className={inputCls}
          onChange={onChange}
          value={value}
          {...props}
        />
      );
    }

    // ── Slots present: wrap in relative container ──────────────────────────────
    return (
      <div className="relative flex w-full items-center">
        {/* ── Start slot ──────────────────────────────────────────────────── */}
        {(startText || startIcon) && (
          <span
            className={cn(
              'pointer-events-none absolute flex items-center text-muted-foreground',
              slotLeft[size],
            )}
          >
            {startText ? (
              <span className={cn(slotText[size], 'leading-none select-none')}>{startText}</span>
            ) : (
              <span className={slotIconSize[size]}>{startIcon}</span>
            )}
          </span>
        )}

        <input
          ref={ref}
          type={type}
          className={inputCls}
          onChange={onChange}
          value={value}
          {...props}
        />

        {/* ── End slot — priority: loading > clear > endText > endIcon ─────── */}
        {loading ? (
          <span
            className={cn(
              'pointer-events-none absolute flex items-center text-muted-foreground',
              slotRight[size],
            )}
          >
            <Loader2 className={cn(slotIconSize[size], 'animate-spin')} />
          </span>
        ) : showClear ? (
          <button
            type="button"
            tabIndex={-1}
            aria-label="Clear"
            onClick={handleClear}
            className={cn(
              'absolute flex items-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none',
              slotRight[size],
            )}
          >
            <X className={slotIconSize[size]} />
          </button>
        ) : endText ? (
          <span
            className={cn(
              'pointer-events-none absolute flex items-center text-muted-foreground select-none',
              slotText[size],
              slotRight[size],
            )}
          >
            {endText}
          </span>
        ) : endIcon ? (
          <span
            className={cn(
              'pointer-events-none absolute flex items-center text-muted-foreground',
              slotRight[size],
            )}
          >
            <span className={slotIconSize[size]}>{endIcon}</span>
          </span>
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { Input };
